'use client';

import Link from 'next/link';
import BlogCard from './BlogCard';
import { useLanguage } from '@/contexts/LanguageContext';
import RichText from '@/components/landing/RichText';

/**
 * The category values the API filters on.
 *
 * Deliberately NOT translated and deliberately NOT derived from the visible
 * tab labels: these strings are the query the blog API matches against, and a
 * Hindi reader filtering by "फीस प्रबंधन" has to end up at the same posts as
 * an English reader filtering by "Fee Management". The labels live in
 * `blog.categoryTabs`, index-aligned with this list; the empty first entry is
 * the "all posts" tab, which carries no category at all.
 */
const BLOG_CATEGORIES = ['', 'School Operations', 'Fee Management', 'Buying Guide'];

/**
 * The marketing blog index body.
 *
 * Split out of the route so the page itself can stay a Server Component — it
 * still does the host branching, the metadata and the post fetch, all of which
 * have to run on the server. Only this chrome needs the language context, and
 * only this chrome is translated: post titles and excerpts render exactly as
 * their author wrote them.
 */
export default function BlogIndex({ posts = [], activeCategory = '' }) {
  const { t, tv } = useLanguage();
  const tabLabels = tv('blog.categoryTabs');

  return (
    <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-10">
      <header className="mb-10 max-w-3xl">
        <h1 className="font-sora text-[38px] font-black leading-tight tracking-tight text-[#0d1117] sm:text-[46px]">
          <RichText text={t('blog.heading')} accentClassName="text-gradient-blue" />
        </h1>
        <p className="mt-3 text-lg leading-relaxed text-[#596875]">{t('blog.subtitle')}</p>

        {/* Category Navigation Pills */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {BLOG_CATEGORIES.map((value, i) => {
            const isActive =
              activeCategory.toLowerCase() === value.toLowerCase() || (!activeCategory && !value);
            return (
              <Link
                key={value || 'all'}
                href={value ? `/blog?category=${encodeURIComponent(value)}` : '/blog'}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${
                  isActive
                    ? 'bg-[#3D5EE1] text-white shadow-md'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-[#3D5EE1] hover:text-[#3D5EE1]'
                }`}
              >
                {tabLabels?.[i] ?? value}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Active Category Filter Notice (when navigated from Support or filtered) */}
      {activeCategory ? (
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#3D5EE1]/5 border border-[#3D5EE1]/20 px-5 py-3.5 shadow-2xs">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-700">
            <span className="text-slate-500">{t('blog.filterNotice')}</span>
            <span className="font-sora rounded-lg bg-[#3D5EE1] px-3 py-1 font-bold text-white">
              {/* The chip shows the TRANSLATED label where we have one, but
                  falls back to the raw query value — a category typed straight
                  into the URL has no dictionary entry and should still read
                  back as whatever was asked for. */}
              {tabLabels?.[BLOG_CATEGORIES.findIndex(
                (c) => c.toLowerCase() === activeCategory.toLowerCase()
              )] ?? activeCategory}
            </span>
          </div>
          <Link href="/blog" className="text-xs font-bold text-[#3D5EE1] hover:underline">
            {t('blog.clearFilter')}
          </Link>
        </div>
      ) : null}

      {posts.length === 0 ? (
        <div
          className="rounded-[24px] border border-slate-100 bg-white px-6 py-16 text-center shadow-[0_4px_25px_rgba(0,0,0,0.03)]"
          data-testid="blog-empty"
        >
          <p className="font-bold text-[#1e293b]">{t('blog.emptyTitle')}</p>
          <p className="mt-2 text-sm text-[#7a8a99]">{t('blog.emptyBody')}</p>
          <Link
            href="/blog"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3D5EE1] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#2A48C9]"
          >
            {t('blog.viewAll')}
          </Link>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="blog-grid"
        >
          {posts.map((p) => (
            <BlogCard key={p.slug} post={p} />
          ))}
        </div>
      )}
    </main>
  );
}
